// Updated GCPGuide component (FINAL VERSION)
import React, { useState } from "react";
import { Box, Paper, Typography, TextField, Button } from "@mui/material";
import CodeBlock from "../../../../../components/CodeBlock/CodeBlock";

const SERVICE_NAME = "solsphereAudit";

export default function GCPGuide() {
  const [projectId, setProjectId] = useState("");
  const [stage, setStage] = useState(1);

  const handleGenerate = () => {
    if (!projectId.trim()) return;
    setStage(2);
  };

  return (
    <Box sx={{ mt: 2, px: 1 }}>
      {/* Step 1 */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium">
          Step 1: Install Google Cloud CLI
        </Typography>

        <Typography variant="body2">For macOS (using Homebrew):</Typography>
        <CodeBlock content="brew install --cask google-cloud-sdk" />

        <Typography variant="body2" sx={{ mt: 1 }}>
          Other operating systems:
          <a
            href="https://cloud.google.com/sdk/docs/install"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              marginLeft: 4,
              color: "#1976d2",
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            Refer to the official documentation
          </a>
        </Typography>
      </Paper>

      {/* Step 2 */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium">
          Step 2: Sign In to Google Cloud
        </Typography>

        <Typography variant="body2">
          This command opens a Google sign-in page. Log in with the Google
          account that manages your Cloud Project.
        </Typography>

        <CodeBlock content="gcloud auth application-default login" />
      </Paper>

      {/* Step 3 — Project ID Input */}
      {stage === 1 && (
        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Step 3: Enter Your Project ID
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            Your Project ID looks like: <b>my-company-123</b>. You can find this
            in the Google Cloud Dashboard (top-left corner).
          </Typography>

          <TextField
            fullWidth
            label="Project ID"
            size="small"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          />

          <Button variant="contained" onClick={handleGenerate} sx={{ mt: 2 }}>
            Generate Setup Commands
          </Button>
        </Paper>
      )}

      {/* Step 4 Onwards */}
      {stage === 2 && (
        <>
          {/* Step 4 */}
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              Step 4: Select Your Project in Google Cloud CLI
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              This tells Google Cloud CLI which project you want to work with.
            </Typography>
            <CodeBlock content={`gcloud config set project ${projectId}`} />
          </Paper>

          {/* Step 5 */}
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              Step 5: Enable Service Usage API
            </Typography>

            <Typography variant="body2" sx={{ mt: 1 }}>
              This API is required for solsphere to verify your project access.
            </Typography>
            <CodeBlock content="gcloud services enable serviceusage.googleapis.com" />
          </Paper>

          {/* Step 6 */}
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              Step 6: Create a Service Account
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              This service account will allow solsphere to read your cloud
              configuration. It does NOT change anything in your project.
            </Typography>

            <CodeBlock
              content={`gcloud iam service-accounts create solsphereAudit \\
  --description="solsphere Audit" \\
  --display-name="solsphereAudit"`}
            />
          </Paper>

          {/* Step 7 */}
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              Step 7: Give Required Permissions
            </Typography>

            <Typography variant="body2" sx={{ mb: 1 }}>
              These roles only allow solsphere to view your project
              configuration. No write or delete permissions are added.
            </Typography>

            <CodeBlock
              content={`gcloud projects add-iam-policy-binding ${projectId} \\
  --member="serviceAccount:solsphereaudit@${projectId}.iam.gserviceaccount.com" \\
  --role="roles/viewer"`}
            />

            <CodeBlock
              content={`gcloud projects add-iam-policy-binding ${projectId} \\
  --member="serviceAccount:solsphereaudit@${projectId}.iam.gserviceaccount.com" \\
  --role="roles/serviceusage.serviceUsageConsumer"`}
            />
          </Paper>

          {/* Step 8 */}
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              Step 8: Create and Download the Key File
            </Typography>

            <Typography variant="body2" sx={{ mb: 1 }}>
              This file will be uploaded in the solsphere dashboard to complete
              the integration.
            </Typography>

            <CodeBlock
              content={`gcloud iam service-accounts keys create solsphereAuditKey.json \\
  --iam-account="solsphereaudit@${projectId}.iam.gserviceaccount.com"`}
            />
          </Paper>

          {/* Step 9 – Check JSON File */}
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              Step 9: Check Your JSON File
            </Typography>

            <Typography variant="body2" sx={{ mb: 1 }}>
              Before uploading the key file, please make sure it looks valid.
              You can check it using the command below. If the file opens and
              shows readable JSON, everything is correct.
            </Typography>

            <CodeBlock content={`cat solsphereAuditKey.json`} />

            <Typography variant="body2" sx={{ mt: 1 }}>
              When you run this, you should see a structured JSON that contains
              fields like:
              <br />
              <b>"client_email"</b>, <b>"private_key"</b>, <b>"project_id"</b>
              <br />
              If you see these fields, your key is valid and ready to upload.
            </Typography>
          </Paper>
        </>
      )}
    </Box>
  );
}
